import { IconButton } from "@mui/material";
import { memo, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import {
  allMessagesStore,
  setAllMessages,
} from "../../../Store/Slices/socket/AllMessagesSlice";
import { socketStatusStore } from "../../../Store/Slices/socket/SocketSlice";
import {
  StyledBannerCarouselSlickSlider,
  StyledBannerCarouselWrapper,
  StyledReactVideoPlayer,
} from "../../Styles/StyledComponents";
import { SOCKET_CONNECTION_TYPES } from "../../utils/Enum";
import { AppMaterialIcons } from "../Icons";
import { botInfoStore } from "../../../Store/Slices/socket/BotInfoSlice";
import useMessagesData from "../../hooks/useMessagesData";

interface Props {}

const AppRenderBannerCarousel: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const { sessionId, allMessages } = useAppSelector(allMessagesStore);
  const { socket } = useAppSelector(socketStatusStore);
  const { botConfig } = useSelector(useBotConfigStore);
  const [data, setData] = useState<any[]>([]);
  const { isBotSetToFullScreen } = useSelector(botInfoStore);
  const { postBotUserQuery } = useMessagesData();
  const videoRefs = useRef<{ [key: number]: any }>({});
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    if (
      botConfig?.config?.banner_carousel?.enabled &&
      botConfig?.config?.banner_carousel?.data
    ) {
      setData(botConfig?.config?.banner_carousel?.data);
    }
  }, [botConfig]);

  // Cleanup effect to stop all videos when component unmounts
  useEffect(() => {
    return () => {
      data.forEach((item: any, index: number) => {
        if (item?.View_options === "Video" && item?.video) {
          stopAndCleanupVideo(index);
        }
      });
    };
  }, [data]);

  // Effect to stop videos when current slide changes
  useEffect(() => {
    data.forEach((item: any, index: number) => {
      if (index !== currentSlide && item?.View_options === "Video" && item?.video) {
        stopAndCleanupVideo(index);
      }
    });
  }, [currentSlide, data]);

  const handleCardClick = (url: any) => {
    window.open(url, "_blank", "noreferrer");
  };

  const handleOptionBtnClick = async (item: any) => {
    let data = {
      type: "text",
      value:
        item?.journey?.sub_flow?.response?.[0]?.value ||
        item?.journey?.main_flow?.name,
      journey: {
        nodeId: item?.journey?.sub_flow?.nodeId,
        qid: item?.journey?.sub_flow?.qid,
        stage: item?.journey?.sub_flow?.stage,
        displayOrder: item?.journey?.sub_flow?.displayOrder,
      },
      session_id: sessionId,
      isUser: true,
    };

    // dispatch(setAllMessages([data]));
    postBotUserQuery(data);
    // if (socket?.connected) {
    //   socket?.emit(SOCKET_CONNECTION_TYPES.BOT_CONVERSATION, data);
    // }
  };



  const stopAndCleanupVideo = (videoIndex: number) => {
    const videoRef = videoRefs.current[videoIndex];
    if (videoRef?.getInternalPlayer) {
      try {
        const player = videoRef.getInternalPlayer();
        if (player?.stopVideo) {
          player.stopVideo(); // Completely stop the video
        }
        if (player?.pauseVideo) {  
          player.pauseVideo(); // Also pause for good measure
        }
      } catch (error) {
        console.log("Error stopping YouTube video:", error);
      }
    }
  };

  const handleBeforeChange = (oldIndex: number, newIndex: number) => {
    // Stop and cleanup the video that we're leaving
    const oldSlideItem = data[oldIndex];
    if (oldSlideItem?.View_options === "Video" && oldSlideItem?.video) {
      stopAndCleanupVideo(oldIndex);
    }
    setCurrentSlide(newIndex);
  };

  const handleAfterChange = (currentSlideIndex: number) => {
    setCurrentSlide(currentSlideIndex);
    // Ensure all other videos are stopped when we arrive at a new slide
    data.forEach((item: any, index: number) => {
      if (index !== currentSlideIndex && item?.View_options === "Video" && item?.video) {
        stopAndCleanupVideo(index);
      }
    });
  };

  if (
    botConfig?.config?.banner_carousel?.data?.length < 1 ||
    !botConfig?.config?.banner_carousel?.enabled ||
    allMessages?.length < 1 ||
    allMessages.find((item: any) => item?.isUser === true)
  ) {
    return null;
  }

  return (
    <StyledBannerCarouselWrapper isFullScreen={isBotSetToFullScreen}>
      <StyledBannerCarouselSlickSlider
        isFullScreen={isBotSetToFullScreen}
        {...settings}
        beforeChange={handleBeforeChange}
        afterChange={handleAfterChange}
      >
        {data?.length &&
          data?.map((item: any, index: number) => {
            return (
              <div
                className="sliderSingleItem"
                key={index.toString()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (item?.View_options !== "Video" &&
                      Object.keys(item?.journey)?.length &&
                      item?.position !== "Add Link") {
                    handleOptionBtnClick(item);
                  } else if (item?.View_options !== "Video") {
                    handleCardClick(item?.banner_url);
                  }
                }}
              >
                {item?.video && item?.View_options === "Video" ? (
                  <StyledReactVideoPlayer
                    key={`video-${index}-${currentSlide}`} // Force re-creation when slide changes
                    ref={(ref: any) => {
                      if (ref) {
                        videoRefs.current[index] = ref;
                      } else {
                        // Clean up ref when component unmounts
                        delete videoRefs.current[index];
                      }
                    }}
                    isFullScreen={isBotSetToFullScreen}
                    url={item?.video}
                    width={"100%"}
                    controls={true}
                    light={false}
                    playing={currentSlide === index} // Only play if current slide
                    onClickPreview={(event: any) => {
                      event?.preventDefault?.();
                      event?.stopPropagation?.();
                      handleCardClick(item?.video);
                    }}
                    onReady={(player: any) => {
                      // Store video player reference for pause control
                      if (videoRefs.current[index]) {
                        videoRefs.current[index].player = player;
                      }
                      // Stop video immediately if not current slide
                      if (currentSlide !== index && player?.stopVideo) {
                        player.stopVideo();
                      }
                    }}
                    onPlay={() => {
                      // Only allow play if this is the current slide
                      if (currentSlide !== index) {
                        stopAndCleanupVideo(index);
                        return;
                      }
                      // Stop other YouTube videos when this one starts playing
                      data.forEach((videoItem: any, videoIndex: number) => {
                        if (videoIndex !== index && videoItem?.View_options === "Video" && videoItem?.video) {
                          stopAndCleanupVideo(videoIndex);
                        }
                      });
                    }}
                    onPause={() => {
                      // Ensure video is truly paused
                      const videoRef = videoRefs.current[index];
                      if (videoRef?.getInternalPlayer) {
                        try {
                          const player = videoRef.getInternalPlayer();
                          if (player?.pauseVideo) {
                            player.pauseVideo();
                          }
                        } catch (error) {
                          console.log("Error ensuring video pause:", error);
                        }
                      }
                    }}
                    onEnded={() => {
                      // Clean up when video ends
                      stopAndCleanupVideo(index);
                    }}
                  />
                ) : (
                  <img src={item?.image?.source} alt="" />
                )}
              </div>
            );
          })}
      </StyledBannerCarouselSlickSlider>
    </StyledBannerCarouselWrapper>
  );
};

export default memo(AppRenderBannerCarousel);

const CustomNextArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <IconButton onClick={onClick} className={className} style={{ ...style }}>
      <AppMaterialIcons iconName={"ChevronRight"} />
    </IconButton>
  );
};

const CustomPrevArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <IconButton onClick={onClick} className={className} style={{ ...style }}>
      <AppMaterialIcons iconName={"ChevronLeft"} />
    </IconButton>
  );
};

const settings = {
  className: "slickSlider",
  adaptiveHeight: true,
  lazyLoad: true,
  fade: false, // Disable fade to prevent click event issues
  dots: true,
  infinite: true,
  speed: 500,
  autoplay: true,
  autoplaySpeed: 5000, // Increased autoplay speed for video content
  pauseOnHover: true, // Enable pause on hover
  pauseOnFocus: true, // Pause when focused
  pauseOnDotsHover: true, // Pause when hovering on dots
  swipeToSlide: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  initialSlide: 0,
  touchThreshold: 10, // Reduce touch sensitivity to prevent accidental swipes
  accessibility: true,
  focusOnSelect: false, // Prevent focus issues with click events

  nextArrow: <CustomNextArrow />,
  prevArrow: <CustomPrevArrow />,
};
